import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from 'xlsx';

const Hackathon = ({ role }) => {
  const [participants, setParticipants] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [submissionsPagination, setSubmissionsPagination] = useState({});
  const [authToken, setAuthToken] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('participants');
  
  // Hackathon ID - you can modify this or make it dynamic
  const hackathonId = "0b6698e8-4af8-4553-98d8-e0bf69e96d39";

  // Function to authenticate and get bearer token
  const authenticateUser = async () => {
    setLoginLoading(true);
    try {
      const response = await axios.post('https://api.hackathon-cms.aaruush.org/api/auth/login', {
        email: "envision@aaruush.org",
        password: "envision@cms25"
      });

      if (response.data.success && response.data.data.token) {
        const token = response.data.data.token;
        setAuthToken(token);
        console.log('Authentication successful');
        return token;
      } else {
        throw new Error('Authentication failed');
      }
    } catch (error) {
      console.error("Authentication error:", error);
      alert("Failed to authenticate. Please check console for details.");
      return null;
    } finally {
      setLoginLoading(false);
    }
  };

  // Function to fetch all participants with pagination
  const fetchAllParticipants = async () => {
    setLoading(true);
    try {
      let token = authToken;
      
      // If no token, authenticate first
      if (!token) {
        token = await authenticateUser();
        if (!token) {
          return;
        }
      }

      let allParticipants = [];
      let page = 1;
      let hasNext = true;

      while (hasNext) {
        const response = await axios.get(
          `https://api.hackathon-cms.aaruush.org/api/admin/users?page=${page}&limit=50&role=participant`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data.success) {
          const { users, pagination: paginationData } = response.data.data;
          
          allParticipants = [...allParticipants, ...users];
          
          setPagination(paginationData);
          hasNext = paginationData.hasNext;
          page++;
        } else {
          throw new Error('Failed to fetch participants');
        }
      }

      setParticipants(allParticipants);
      console.log(`Fetched ${allParticipants.length} participants`);
    } catch (error) {
      console.error("Error fetching hackathon participants:", error);
      
      // If unauthorized, try to re-authenticate
      if (error.response && error.response.status === 401) {
        console.log("Token expired, re-authenticating...");
        setAuthToken(null);
        const newToken = await authenticateUser();
        if (newToken) {
          // Retry fetching with new token
          await fetchAllParticipants();
        }
      } else {
        alert("Error fetching participants. Please check console for details.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch all submissions with pagination
  const fetchAllSubmissions = async () => {
    setSubmissionsLoading(true);
    try {
      let token = authToken;
      
      // If no token, authenticate first
      if (!token) {
        token = await authenticateUser();
        if (!token) {
          return;
        }
      }

      let allSubmissions = [];
      let page = 1;
      let hasNext = true;

      while (hasNext) {
        const response = await axios.get(
          `https://api.hackathon-cms.aaruush.org/api/submissions/hackathon/${hackathonId}?page=${page}&limit=50`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data.success) {
          const { data, pagination: paginationData } = response.data;
          
          allSubmissions = [...allSubmissions, ...data];
          
          setSubmissionsPagination(paginationData);
          hasNext = page < paginationData.totalPages;
          page++;
        } else {
          throw new Error('Failed to fetch submissions');
        }
      }

      setSubmissions(allSubmissions);
      console.log(`Fetched ${allSubmissions.length} submissions`);
    } catch (error) {
      console.error("Error fetching hackathon submissions:", error);
      
      // If unauthorized, try to re-authenticate
      if (error.response && error.response.status === 401) {
        console.log("Token expired, re-authenticating...");
        setAuthToken(null);
        const newToken = await authenticateUser();
        if (newToken) {
          // Retry fetching with new token
          await fetchAllSubmissions();
        }
      } else {
        alert("Error fetching submissions. Please check console for details.");
      }
    } finally {
      setSubmissionsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllParticipants();
  }, []);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "NA";
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Process social links for display
  const processSocialLinks = (socialLinks) => {
    if (!socialLinks || Object.keys(socialLinks).length === 0) return "NA";
    return Object.entries(socialLinks)
      .map(([platform, url]) => `${platform}: ${url}`)
      .join(", ");
  };

  // Process team IDs for display
  const processTeamIds = (teamIds) => {
    if (!teamIds || Object.keys(teamIds).length === 0) return "NA";
    return Object.entries(teamIds)
      .map(([teamId, memberId]) => `Team: ${teamId}`)
      .join(", ");
  };

  // Helper function to identify team leader
  const getTeamLeaderInfo = (submission) => {
    const leader = submission.memberDetails?.find(member => member.id === submission.teamLeader);
    return leader ? `${leader.firstName} ${leader.lastName}` : "Unknown";
  };

  // Helper function to format member details for display
  const formatMemberDetails = (memberDetails, teamLeader) => {
    if (!memberDetails || memberDetails.length === 0) return "No members";
    
    return memberDetails.map(member => {
      const isLeader = member.id === teamLeader;
      return `${member.firstName} ${member.lastName}${isLeader ? ' (Leader)' : ''} - ${member.email}`;
    }).join('; ');
  };

  // Function to download data as Excel
  const downloadExcel = () => {
    if (participants.length === 0) {
      alert('No data to download. Please fetch participants first.');
      return;
    }

    try {
      // Prepare data for Excel
      const excelData = participants.map(participant => ({
        'ID': participant.id || 'NA',
        'Email': participant.email || 'NA',
        'First Name': participant.firstName || 'NA',
        'Last Name': participant.lastName || 'NA',
        'Role': participant.role || 'NA',
        'Skills': participant.skills ? participant.skills.join('; ') : 'NA',
        'Social Links': processSocialLinks(participant.socialLinks),
        'Is Active': participant.isActive ? 'Yes' : 'No',
        'Is Verified': participant.isVerified ? 'Yes' : 'No',
        'Team IDs': processTeamIds(participant.teamIds),
        'Created At': formatDate(participant.createdAt),
        'Updated At': formatDate(participant.updatedAt)
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 10 }, // ID
        { wch: 25 }, // Email
        { wch: 15 }, // First Name
        { wch: 15 }, // Last Name
        { wch: 12 }, // Role
        { wch: 30 }, // Skills
        { wch: 40 }, // Social Links
        { wch: 10 }, // Is Active
        { wch: 12 }, // Is Verified
        { wch: 20 }, // Team IDs
        { wch: 20 }, // Created At
        { wch: 20 }  // Updated At
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Hackathon Participants');

      // Generate filename with current date
      const filename = `Hackathon_Participants_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Write and download the file
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      alert('Error downloading Excel file. Please try again.');
    }
  };

  // Function to download submissions as Excel
  const downloadSubmissionsExcel = () => {
    if (submissions.length === 0) {
      alert('No submissions data to download. Please fetch submissions first.');
      return;
    }

    try {
      // Prepare data for Excel - create individual rows for each team member
      const excelData = [];
      
      submissions.forEach(submission => {
        if (submission.memberDetails && submission.memberDetails.length > 0) {
          submission.memberDetails.forEach((member, memberIndex) => {
            const isLeader = member.id === submission.teamLeader;
            excelData.push({
              'Team Name': submission.teamName || 'NA',
              'Project Title': submission.title || 'NA',
              'Team Leader Name': getTeamLeaderInfo(submission),
              'Member Name': `${member.firstName} ${member.lastName}`,
              'Member Role': isLeader ? 'Team Leader' : 'Member',
              'Member Email': member.email || 'NA',
              'Member Phone': member.phone || 'NA',
              'Member College': member.college || 'NA',
              'Member Year of Study': member.yearOfStudy || 'NA',
              'Member ID': member.id || 'NA',
              'Is Verified': member.isVerified ? 'Yes' : 'No',
              'Description': memberIndex === 0 ? (submission.description || 'NA') : '', // Only show description for first member
              'GitHub URL': memberIndex === 0 ? (submission.githubUrl || 'NA') : '',
              'PPT/PDF Link': memberIndex === 0 ? (submission.files?.pdf || 'NA') : '',
              'Theme Selected': memberIndex === 0 ? (submission.themeSelected || 'NA') : '',
              'Status': memberIndex === 0 ? (submission.status || 'NA') : '',
              'Team Status': memberIndex === 0 ? (submission.teamStatus || 'NA') : '',
              'Is Late': memberIndex === 0 ? (submission.isLate ? 'Yes' : 'No') : '',
              'Technologies': memberIndex === 0 ? (submission.technologies ? submission.technologies.join('; ') : 'NA') : '',
              'Submitted At': memberIndex === 0 ? formatDate(submission.submittedAt) : '',
              'Max Members': memberIndex === 0 ? (submission.maxMembers || 'NA') : '',
              'Team ID': memberIndex === 0 ? (submission.teamId || 'NA') : '',
              'Submission ID': memberIndex === 0 ? (submission.id || 'NA') : ''
            });
          });
        } else {
          // If no member details, create a single row
          excelData.push({
            'Team Name': submission.teamName || 'NA',
            'Project Title': submission.title || 'NA',
            'Team Leader Name': 'No member details available',
            'Member Name': 'No member details available',
            'Member Role': 'NA',
            'Member Email': 'NA',
            'Member Phone': 'NA',
            'Member College': 'NA',
            'Member Year of Study': 'NA',
            'Member ID': 'NA',
            'Is Verified': 'NA',
            'Description': submission.description || 'NA',
            'GitHub URL': submission.githubUrl || 'NA',
            'PPT/PDF Link': submission.files?.pdf || 'NA',
            'Theme Selected': submission.themeSelected || 'NA',
            'Status': submission.status || 'NA',
            'Team Status': submission.teamStatus || 'NA',
            'Is Late': submission.isLate ? 'Yes' : 'No',
            'Technologies': submission.technologies ? submission.technologies.join('; ') : 'NA',
            'Submitted At': formatDate(submission.submittedAt),
            'Max Members': submission.maxMembers || 'NA',
            'Team ID': submission.teamId || 'NA',
            'Submission ID': submission.id || 'NA'
          });
        }
      });

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 20 }, // Team Name
        { wch: 30 }, // Project Title
        { wch: 25 }, // Team Leader Name
        { wch: 25 }, // Member Name
        { wch: 15 }, // Member Role
        { wch: 30 }, // Member Email
        { wch: 15 }, // Member Phone
        { wch: 35 }, // Member College
        { wch: 15 }, // Member Year of Study
        { wch: 25 }, // Member ID
        { wch: 12 }, // Is Verified
        { wch: 50 }, // Description
        { wch: 30 }, // GitHub URL
        { wch: 50 }, // PPT/PDF Link
        { wch: 20 }, // Theme Selected
        { wch: 15 }, // Status
        { wch: 15 }, // Team Status
        { wch: 10 }, // Is Late
        { wch: 30 }, // Technologies
        { wch: 20 }, // Submitted At
        { wch: 12 }, // Max Members
        { wch: 25 }, // Team ID
        { wch: 25 }  // Submission ID
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Hackathon Submissions');

      // Generate filename with current date
      const filename = `Hackathon_Submissions_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Write and download the file
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error('Error downloading submissions Excel file:', error);
      alert('Error downloading submissions Excel file. Please try again.');
    }
  };

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <h2 className="my-4">Hackathon Management</h2>
            
            {/* Tab Navigation */}
            <ul className="nav nav-tabs mb-4" id="hackathonTabs" role="tablist">
              <li className="nav-item" role="presentation">
                <button 
                  className={`nav-link ${activeTab === 'participants' ? 'active' : ''}`}
                  onClick={() => setActiveTab('participants')}
                  type="button"
                >
                  Participants ({participants.length})
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button 
                  className={`nav-link ${activeTab === 'submissions' ? 'active' : ''}`}
                  onClick={() => setActiveTab('submissions')}
                  type="button"
                >
                  Submissions ({submissions.length})
                </button>
              </li>
            </ul>

            {/* Participants Tab */}
            {activeTab === 'participants' && (
              <>
                {/* Statistics and Export Button */}
                <div className="card mb-4">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <h5>Total Participants: {participants.length}</h5>
                        <p className="text-muted">
                          Active: {participants.filter(p => p.isActive).length} | 
                          Verified: {participants.filter(p => p.isVerified).length}
                        </p>
                        <div className="mt-2">
                          <span className={`badge ${authToken ? 'bg-success' : 'bg-warning'}`}>
                            {authToken ? 'Authenticated' : 'Not Authenticated'}
                          </span>
                          {pagination.total && (
                            <span className="badge bg-info ms-2">
                              Total in API: {pagination.total}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6 text-end">
                        {!authToken && (
                          <button 
                            className="btn btn-warning me-2" 
                            onClick={authenticateUser}
                            disabled={loginLoading}
                          >
                            {loginLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Authenticating...
                              </>
                            ) : (
                              'Authenticate'
                            )}
                          </button>
                        )}
                        
                        <button 
                          className="btn btn-primary me-2" 
                          onClick={fetchAllParticipants}
                          disabled={loading || !authToken}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Refreshing...
                            </>
                          ) : (
                            'Refresh Data'
                          )}
                        </button>
                        
                        <button 
                          type="button" 
                          className="btn btn-success"
                          disabled={participants.length === 0}
                          onClick={downloadExcel}
                        >
                          Download Excel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Participants List */}
                <div className="card">
                  <div className="card-header">
                    <h5>Participants List</h5>
                  </div>
                  <div className="card-body">
                    {loading ? (
                      <div className="text-center">
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading participants...</p>
                      </div>
                    ) : !authToken ? (
                      <div className="text-center">
                        <div className="alert alert-warning" role="alert">
                          <h5>Authentication Required</h5>
                          <p>Please authenticate first to fetch hackathon participants.</p>
                          <button 
                            className="btn btn-warning" 
                            onClick={authenticateUser}
                            disabled={loginLoading}
                          >
                            {loginLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Authenticating...
                              </>
                            ) : (
                              'Authenticate Now'
                            )}
                          </button>
                        </div>
                      </div>
                    ) : participants.length === 0 ? (
                      <div className="text-center">
                        <div className="alert alert-info" role="alert">
                          <p>No participants found. Click "Refresh Data" to fetch participants.</p>
                        </div>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-striped table-hover">
                          <thead className="table-dark">
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Role</th>
                              <th>Status</th>
                              <th>Skills</th>
                              <th>Social Links</th>
                              <th>Created At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {participants
                              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                              .map((participant, index) => (
                                <tr key={participant.id || index}>
                                  <td>
                                    <strong>{participant.firstName} {participant.lastName}</strong>
                                  </td>
                                  <td>{participant.email}</td>
                                  <td>
                                    <span className="badge bg-primary">{participant.role}</span>
                                  </td>
                                  <td>
                                    <span className={`badge ${participant.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                      {participant.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                    <span className={`badge ms-1 ${participant.isVerified ? 'bg-info' : 'bg-warning'}`}>
                                      {participant.isVerified ? 'Verified' : 'Unverified'}
                                    </span>
                                  </td>
                                  <td>
                                    {participant.skills && participant.skills.length > 0 
                                      ? participant.skills.join(", ") 
                                      : "No skills listed"
                                    }
                                  </td>
                                  <td>
                                    {participant.socialLinks && Object.keys(participant.socialLinks).length > 0 ? (
                                      <div>
                                        {participant.socialLinks.github && (
                                          <a 
                                            href={participant.socialLinks.github} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="badge bg-dark text-decoration-none me-1"
                                          >
                                            GitHub
                                          </a>
                                        )}
                                        {participant.socialLinks.linkedin && (
                                          <a 
                                            href={participant.socialLinks.linkedin} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="badge bg-primary text-decoration-none"
                                          >
                                            LinkedIn
                                          </a>
                                        )}
                                      </div>
                                    ) : (
                                      "No links"
                                    )}
                                  </td>
                                  <td>{formatDate(participant.createdAt)}</td>
                                </tr>
                              ))
                            }
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Submissions Tab */}
            {activeTab === 'submissions' && (
              <>
                {/* Submissions Statistics and Export Button */}
                <div className="card mb-4">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <h5>Total Submissions: {submissions.length}</h5>
                        <p className="text-muted">
                          Submitted: {submissions.filter(s => s.status === 'submitted').length} | 
                          Late Submissions: {submissions.filter(s => s.isLate).length}
                        </p>
                        <div className="mt-2">
                          <span className={`badge ${authToken ? 'bg-success' : 'bg-warning'}`}>
                            {authToken ? 'Authenticated' : 'Not Authenticated'}
                          </span>
                          {submissionsPagination.total && (
                            <span className="badge bg-info ms-2">
                              Total in API: {submissionsPagination.total}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6 text-end">
                        {!authToken && (
                          <button 
                            className="btn btn-warning me-2" 
                            onClick={authenticateUser}
                            disabled={loginLoading}
                          >
                            {loginLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Authenticating...
                              </>
                            ) : (
                              'Authenticate'
                            )}
                          </button>
                        )}
                        
                        <button 
                          className="btn btn-primary me-2" 
                          onClick={fetchAllSubmissions}
                          disabled={submissionsLoading || !authToken}
                        >
                          {submissionsLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Loading Submissions...
                            </>
                          ) : (
                            'Load Submissions'
                          )}
                        </button>
                        
                        <button 
                          type="button" 
                          className="btn btn-success"
                          disabled={submissions.length === 0}
                          onClick={downloadSubmissionsExcel}
                        >
                          Download Excel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submissions List */}
                <div className="card">
                  <div className="card-header">
                    <h5>Submissions List</h5>
                  </div>
                  <div className="card-body">
                    {submissionsLoading ? (
                      <div className="text-center">
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading submissions...</p>
                      </div>
                    ) : !authToken ? (
                      <div className="text-center">
                        <div className="alert alert-warning" role="alert">
                          <h5>Authentication Required</h5>
                          <p>Please authenticate first to fetch hackathon submissions.</p>
                          <button 
                            className="btn btn-warning" 
                            onClick={authenticateUser}
                            disabled={loginLoading}
                          >
                            {loginLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Authenticating...
                              </>
                            ) : (
                              'Authenticate Now'
                            )}
                          </button>
                        </div>
                      </div>
                    ) : submissions.length === 0 ? (
                      <div className="text-center">
                        <div className="alert alert-info" role="alert">
                          <p>No submissions found. Click "Load Submissions" to fetch submissions.</p>
                        </div>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-striped table-hover">
                          <thead className="table-dark">
                            <tr>
                              <th>Team Name</th>
                              <th>Project Title</th>
                              <th>Description</th>
                              <th>Team Members</th>
                              <th>PPT Link</th>
                              <th>Theme</th>
                              <th>Status</th>
                              <th>Submitted At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {submissions
                              .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
                              .map((submission, index) => (
                                <tr key={submission.id || index}>
                                  <td>
                                    <strong>{submission.teamName}</strong>
                                  </td>
                                  <td>
                                    <div className="fw-bold">{submission.title}</div>
                                  </td>
                                  <td>
                                    <div className="text-muted small">
                                      {submission.description && submission.description.length > 150 
                                        ? `${submission.description.substring(0, 150)}...` 
                                        : submission.description || 'No description'
                                      }
                                    </div>
                                  </td>
                                  <td>
                                    <div className="small">
                                      {submission.memberDetails && submission.memberDetails.map((member, idx) => (
                                        <div key={idx} className="mb-2 p-2 border rounded">
                                          <div className="mb-1">
                                            <span className={`badge ${member.id === submission.teamLeader ? 'bg-warning text-dark' : 'bg-secondary'} me-2`}>
                                              {member.id === submission.teamLeader ? 'Leader' : 'Member'}
                                            </span>
                                            <strong>{member.firstName} {member.lastName}</strong>
                                          </div>
                                          <div className="text-muted small">
                                            <div><strong>Email:</strong> {member.email}</div>
                                            <div><strong>Phone:</strong> {member.phone || 'N/A'}</div>
                                            <div><strong>College:</strong> {member.college || 'N/A'}</div>
                                            <div><strong>Year:</strong> {member.yearOfStudy || 'N/A'}</div>
                                            <div>
                                              <span className={`badge ${member.isVerified ? 'bg-success' : 'bg-warning'}`}>
                                                {member.isVerified ? 'Verified' : 'Unverified'}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                  <td>
                                    {submission.files?.pdf ? (
                                      <a 
                                        href={submission.files.pdf} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline-primary"
                                      >
                                        View PPT
                                      </a>
                                    ) : (
                                      <span className="text-muted">No PPT</span>
                                    )}
                                  </td>
                                  <td>
                                    {submission.themeSelected ? (
                                      <span className="badge bg-info">{submission.themeSelected}</span>
                                    ) : (
                                      <span className="text-muted">No theme</span>
                                    )}
                                  </td>
                                  <td>
                                    <span className={`badge ${submission.status === 'submitted' ? 'bg-success' : 'bg-warning'}`}>
                                      {submission.status}
                                    </span>
                                    {submission.isLate && (
                                      <span className="badge bg-danger ms-1">Late</span>
                                    )}
                                  </td>
                                  <td>{formatDate(submission.submittedAt)}</td>
                                </tr>
                              ))
                            }
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Hackathon;
